/**
 * DetailRightPanel.tsx
 *
 * The record detail column on list pages: inline and collapsible at `lg` and above,
 * inside a right-hand sheet below it, where 400px of detail leaves no usable content
 * column.
 *
 * ── Props and behaviour that stop applying inside a sheet ───────────────────────
 * `defaultCollapsed`, `width`, `expandTrigger` and `toggleTrigger` are **deliberate
 * no-ops** off-canvas, and so is the collapse chevron. The sheet owns visibility,
 * width and dismissal; a second owner is how a panel ends up half-collapsed inside a
 * drawer that is itself open.
 *
 * The AI sidebar is also ignored off-canvas. Inline, this panel force-collapses
 * itself whenever the assistant opens, because they compete for the same screen
 * width. In a sheet they do not compete — the sheet is on top — and letting the
 * assistant collapse a panel whose visibility the sheet owns would give it two
 * masters. Whether opening the assistant *should* dismiss the sheet is an open
 * question in the plan; this is the conservative half of it, and answering it is not
 * this component's call to make unilaterally.
 */
import { CollapseChevron } from '@/components/ui/collapse-chevron';
import { cn } from '@/lib/utils';
import { useAISidebarStore } from '@/store/aiSidebarStore';
import { useEffect, useRef, useState } from 'react';
import { useInOffCanvas } from '@/components/layout/OffCanvasPanel';

interface DetailRightPanelProps {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  width?: string;
  expandTrigger?: number;
  toggleTrigger?: number;
}

export function DetailRightPanel({
  children,
  defaultCollapsed = false,
  width = 'w-[25rem]',
  expandTrigger,
  toggleTrigger,
}: DetailRightPanelProps) {
  const aiSidebarOpen = useAISidebarStore((s) => s.isOpen);
  const closeSidebar = useAISidebarStore((s) => s.closeSidebar);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const prevTrigger = useRef(expandTrigger);
  const prevToggle = useRef(toggleTrigger);
  const inOffCanvas = useInOffCanvas();

  useEffect(() => {
    if (inOffCanvas) return;
    if (expandTrigger !== undefined && expandTrigger !== prevTrigger.current) {
      setCollapsed(false);
      closeSidebar();
      prevTrigger.current = expandTrigger;
    }
  }, [closeSidebar, expandTrigger, inOffCanvas]);

  useEffect(() => {
    if (inOffCanvas) return;
    if (toggleTrigger !== undefined && toggleTrigger !== prevToggle.current) {
      setCollapsed((c) => {
        if (c) closeSidebar();
        return !c;
      });
      prevToggle.current = toggleTrigger;
    }
  }, [closeSidebar, toggleTrigger, inOffCanvas]);

  useEffect(() => {
    if (inOffCanvas) return;
    if (aiSidebarOpen) {
      setCollapsed(true);
    }
  }, [aiSidebarOpen, inOffCanvas]);

  if (inOffCanvas) {
    return <div className="flex h-full min-h-0 w-full flex-col bg-white">{children}</div>;
  }

  return (
    <aside className="relative z-50 flex h-full min-h-0 flex-shrink-0 self-stretch overflow-visible bg-white">
      <CollapseChevron
        collapsed={collapsed}
        onClick={() => {
          setCollapsed((c) => {
            if (c) closeSidebar();
            return !c;
          });
        }}
        side="right"
        title={collapsed ? 'Expand panel' : 'Collapse panel'}
        className="z-40"
      />
      <div
        className={cn(
          'flex h-full min-h-0 flex-shrink-0 self-stretch overflow-hidden transition-[width] duration-200 ease-linear',
          collapsed ? 'w-4 border-l-0' : `border-l border-slate-200 ${width}`,
        )}
      >
        {!collapsed && <div className="flex h-full min-h-0 w-full flex-1 overflow-hidden">{children}</div>}
      </div>
    </aside>
  );
}
